"use client";

import ChatBubbleOvalLeftIcon from "@heroicons/react/24/solid/ChatBubbleOvalLeftIcon";
import UserIcon from "@heroicons/react/24/solid/UserIcon";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import SheetModal from "@/components/ui/sheet-modal";
import { cn } from "@/lib/utils";
import TextArea from "@/components/views/add-place/TextArea";
import { useSession } from "next-auth/react";
import { StarIcon } from "@heroicons/react/24/outline";
import { Input } from "@/components/views/add-place/Input";
import { useVenueDetail } from "@/hooks/venueContext";
import toast from "react-hot-toast";
import { MAX_COMMENT_LENGTH, MIN_COMMENT_LENGTH } from "@/@core/constants/comments";
import { useTranslation } from "@/hooks/translationContext";
import { useSite } from "@/lib/hooks/site";
import ContestDrawer, { ContestFormData } from "@/components/ui/ContestDrawer";
import { registerFormAction } from "@/actions/auth";
import { POST_REGISTER_SESSION_NAME } from "@/@core/constants/auth";
import { updateCommentContestData } from "@/actions/comment/update-contest";

interface Props {
  onClose: VoidFunction;
  isOpen: boolean;
  parentComment: ParentComment;
  handleSubmit: (data) => Promise<boolean | number>;
  disableRating?: boolean;
  isLoading?: boolean;
}


export default function AddReplyModal(props: Props) {
  const [comment, setComment] = useState("");
  const [fullName, setFullName] = useState("");
  const [selectedStar, setSelectedStar] = useState(5);
  const [showContestDrawer, setShowContestDrawer] = useState(false);
  const [pendingCommentData, setPendingCommentData] = useState<any>(null);
  const { venue } = useVenueDetail();
  const { status: sessionStatus, data: session } = useSession();
  const { t } = useTranslation();
  const site = useSite();
  const isCampaignActive = (site?.setting as any)?.campaignIsActive == 1;

  const isButtonDisabled = () => {
    const commentInvalid = comment.length < MIN_COMMENT_LENGTH || comment.length > MAX_COMMENT_LENGTH;
    const nameRequired = sessionStatus === "unauthenticated" && fullName.trim().split(/\s+/).length < 2;
    return commentInvalid || nameRequired;
  };

  const handleSubmit = async () => {
    if (!props.handleSubmit) return;

    if (sessionStatus === "unauthenticated") {
      const nameParts = fullName.trim().split(/\s+/);
      if (nameParts.length < 2 || nameParts.some((p) => !p)) {
        toast.error(t("form-fullname-required-warning"));
        return;
      }
    }

    const replyData = {
      reference_id: venue?.id,
      text: comment,
      rating: selectedStar,
      parent_id: props.parentComment.id,
      fullName: fullName,
    };

    const result = await props.handleSubmit(replyData);
    if (result) {
      props.onClose();
      if (isCampaignActive) {
        const commentId = typeof result === 'number' ? result : null;
        setPendingCommentData({ ...replyData, commentId });
        setShowContestDrawer(true);
      }
    }
  };

  const handleContestSubmit = async (contestData: ContestFormData) => {
    if (!pendingCommentData) {
      return;
    }

    try {
      // Step 1: Save contest data — does not require an account,
      // so it must never be blocked by registration failures
      let contestResult;
      if (pendingCommentData.commentId) {
        contestResult = await updateCommentContestData(
          pendingCommentData.commentId,
          { phone: contestData.phone, instagram_username: contestData.instagram, address: contestData.address, email: contestData.email },
          "venue"
        );
      } else {
        const submitData = {
          ...pendingCommentData,
          phone: contestData.phone,
          instagram_username: contestData.instagram,
          address: contestData.address,
          email: contestData.email,
        };
        contestResult = { success: !!(await props.handleSubmit(submitData)) };
      }

      if (!contestResult.success) {
        toast.error((contestResult as any).error || t("contest-save-error"));
        return;
      }

      setShowContestDrawer(false);
      setPendingCommentData(null);
      toast.success(t("venue-detail-reply-submit-success"));

      // Step 2: Create an account / sign in, best-effort
      if (sessionStatus === "unauthenticated") {
        const registerResult = await registerFormAction({
          nameAndSurname: fullName,
          email: contestData.email,
          password: contestData.password,
          termsOfUse: contestData.termsOfUse,
        });

        if (registerResult?.success) {
          sessionStorage.setItem(POST_REGISTER_SESSION_NAME, "1");
          toast.success(t("user-register-guest-account-success"));
          setTimeout(() => window.location.reload(), 1000);
        } else {
          toast.error(
            registerResult?.error ||
            t("user-register-error")
          );
        }
      }
    } catch (error) {
      toast.error(t("contest-generic-error"));
    }
  }

  useEffect(() => {
    if (sessionStatus === "authenticated" && session?.user) {
      const firstName = session.user.firstname || "";
      const lastName = session.user.lastname || "";
      setFullName(`${firstName} ${lastName}`.trim());
    }
  }, [sessionStatus, session]);

  return (
    <>
      <SheetModal isOpen={props.isOpen} onClose={props.onClose}>
        <div className="py-6 px-2 flex gap-2 items-center">
          <div className="flex-1">
            <span className="text-sm text-assistant font-bold">
              {props.parentComment.fullName} yorumuna/sorusuna yanıt verin
            </span>
            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
              &quot;{props.parentComment.comment}&quot;
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {sessionStatus === "unauthenticated" && (
            <Input
              error={fullName.trim().split(/\s+/).length < 2}
              errorMessage={t("form-fullname-required-warning")}
              onChange={(e) => setFullName(e.target.value)}
              value={fullName}
              icon={<UserIcon className="size-5" />}
              placeholder={t("venue-detail-comment-fullname-placeholder")}
              id="full-name"
            />
          )}

          <TextArea
            error={
              comment.length < MIN_COMMENT_LENGTH ||
              comment.length > MAX_COMMENT_LENGTH
            }
            errorMessage={`En az ${MIN_COMMENT_LENGTH} karakter, en fazla ${MAX_COMMENT_LENGTH} karakter`}
            onChange={(e: { target: { value: string } }) => {
              setComment(e.target.value);
            }}
            className="!h-24"
            icon={<ChatBubbleOvalLeftIcon className="size-5" />}
            placeholder={t("venue-reply-form-placeholder", { defaultValue: "Yanıtınızı yazın..." })}
            id="reply-content"
          />

          {!props.disableRating && (
            <div className="flex items-center gap-4">
              <span className="font-semibold text-assistant">Puanınız:</span>
              <div className="flex gap-2 items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setSelectedStar(star)}
                    aria-label={t("comment-form-rating-star-label", {
                      defaultValue: "{{star}} yıldız ver",
                      star,
                    })}
                    className="focus:outline-none"
                  >
                    <StarIcon
                      className={`w-6 h-6 max-md:w-8 max-md:h-8 ${star <= selectedStar ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                        } transition-colors`}
                    />
                  </button>
                ))}
              </div>
            </div>

          )}

          <Button
            aria-disabled={props.isLoading || isButtonDisabled()}
            disabled={props.isLoading || isButtonDisabled()}
            onClick={handleSubmit}
            variant="accent"
            className={cn(
              `flex font-bold text-sm`,
              isButtonDisabled() ? "cursor-not-allowed" : "",
            )}
          >
            Yanıtı Gönder
          </Button>
        </div>
      </SheetModal>

      <ContestDrawer
        isOpen={showContestDrawer}
        onClose={() => {
          setShowContestDrawer(false);
          setPendingCommentData(null);
        }}
        onSubmit={handleContestSubmit}
        title={t("contest-form-title")}
        description={t("venue-detail-reply-form-description")}
      />
    </>
  );
}